import { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, Inbox } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '@dashboard/hooks/useAuth';
import { getEventConfig, getEventCategories, getKnownEventTypes } from './webhookEventRegistry';
import { getColorClasses } from './eventColors';
import SquareWebhookEventDetail, { type WebhookEvent } from './SquareWebhookEventDetail';

const LIMIT = 50;

type StatusFilter = 'all' | 'processed' | 'unprocessed' | 'error';

export default function SquareWebhookEventsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [apiKey, setApiKey] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [selectedEvent, setSelectedEvent] = useState<WebhookEvent | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('merchants')
      .select('api_key')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.api_key) setApiKey(data.api_key as string);
      });
  }, [user]);

  const loadEvents = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('square_webhook_events')
        .select(
          'id, event_id, event_type, payload, processed, handler_result, processing_error, received_at, processed_at',
          { count: 'exact' }
        )
        .eq('merchant_id', user.id)
        .order('received_at', { ascending: false })
        .range(offset, offset + LIMIT - 1);

      if (filterType) query = query.eq('event_type', filterType);

      const { data, error: err, count } = await query;
      if (err) throw err;
      setEvents((data as WebhookEvent[]) || []);
      setTotal(count || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
      setEvents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [user, offset, filterType]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const filteredEvents = useMemo(() => {
    if (filterStatus === 'all') return events;
    return events.filter((e) => {
      if (filterStatus === 'processed') return e.processed && !e.processing_error;
      if (filterStatus === 'unprocessed') return !e.processed;
      if (filterStatus === 'error') return !!e.processing_error;
      return true;
    });
  }, [events, filterStatus]);

  const categories = useMemo(() => getEventCategories(), []);
  const knownTypes = useMemo(() => getKnownEventTypes(), []);

  const inputStyle: React.CSSProperties = {
    background: 'var(--dash-input)',
    border: '1px solid var(--dash-border)',
    color: 'var(--dash-text-secondary)',
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-sans text-2xl font-medium mb-1" style={{ color: 'var(--dash-text-primary)' }}>
          Square Webhook Events
        </h1>
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>
          All webhook events received from Square, in order.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setOffset(0);
          }}
          className="font-mono text-xs px-3 py-1.5 rounded"
          style={inputStyle}
        >
          <option value="">All event types</option>
          {categories.map((cat) => (
            <optgroup key={cat} label={cat}>
              {knownTypes
                .filter((t) => getEventConfig(t).category === cat)
                .map((t) => (
                  <option key={t} value={t}>
                    {getEventConfig(t).label}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>

        <div className="flex gap-0 rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
          {(['all', 'processed', 'unprocessed', 'error'] as const).map((s) => {
            const isActive = filterStatus === s;
            return (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 transition-colors"
                style={{
                  background: isActive ? 'var(--dash-active-bg)' : 'transparent',
                  color: isActive ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
                }}
              >
                {s}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => loadEvents()}
          className="inline-flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 rounded transition-colors"
          style={{ color: 'var(--dash-text-muted)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--dash-text-primary)';
            e.currentTarget.style.background = 'var(--dash-hover-bg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--dash-text-muted)';
            e.currentTarget.style.background = '';
          }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div
        className="font-mono text-[10px] uppercase tracking-wider mb-3"
        style={{ color: 'var(--dash-text-faint)' }}
      >
        {filteredEvents.length} of {total} events
      </div>

      {error && (
        <div className="mb-4 p-3 text-xs bg-red-500/10 border border-red-500/30 rounded text-red-400 font-mono">
          {error}
        </div>
      )}

      {loading ? (
        <div
          className="text-center py-12 font-mono text-xs"
          style={{ color: 'var(--dash-text-muted)' }}
        >
          Loading events{'\u2026'}
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="text-center py-12">
          <Inbox size={32} className="mx-auto mb-3 opacity-30" style={{ color: 'var(--dash-text-muted)' }} />
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>
            No events yet
          </p>
          <p className="font-sans text-xs mt-1" style={{ color: 'var(--dash-text-faint)' }}>
            Events will appear here when Square sends webhooks to your endpoint.
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {filteredEvents.map((event) => (
            <EventListItem key={event.id} event={event} onClick={() => setSelectedEvent(event)} />
          ))}
        </div>
      )}

      {total > LIMIT && (
        <div className="flex items-center justify-between mt-6 font-mono text-xs">
          <button
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            disabled={offset === 0}
            className="transition-colors disabled:opacity-30"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            {'\u2190'} Previous
          </button>
          <span style={{ color: 'var(--dash-text-muted)' }}>
            {offset + 1}{'\u2013'}{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <button
            onClick={() => setOffset(offset + LIMIT)}
            disabled={offset + LIMIT >= total}
            className="transition-colors disabled:opacity-30"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            Next {'\u2192'}
          </button>
        </div>
      )}

      {selectedEvent && (
        <SquareWebhookEventDetail
          event={selectedEvent}
          apiKey={apiKey}
          onClose={() => setSelectedEvent(null)}
          onReplayed={() => {
            setSelectedEvent(null);
            loadEvents();
          }}
        />
      )}
    </div>
  );
}

function EventListItem({ event, onClick }: { event: WebhookEvent; onClick: () => void }) {
  const config = getEventConfig(event.event_type);
  const colors = getColorClasses(config.color);
  const isUnknown = !getKnownEventTypes().includes(event.event_type);
  const hasError = !!event.processing_error;

  const summary =
    config.renderSummary?.(event.payload?.data?.object) || event.payload?.data?.id || '';

  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 p-3 rounded transition-colors text-left"
      style={{
        background: 'var(--dash-card)',
        border: '1px solid var(--dash-border)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--dash-hover-bg)';
        e.currentTarget.style.borderColor = 'var(--dash-border-solid)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--dash-card)';
        e.currentTarget.style.borderColor = 'var(--dash-border)';
      }}
    >
      <div
        className={`flex-shrink-0 w-8 h-8 rounded ${colors.bg} ${colors.border} border flex items-center justify-center font-mono text-sm ${colors.text}`}
      >
        {config.symbol}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="font-sans text-sm" style={{ color: 'var(--dash-text-primary)' }}>
            {config.label}
          </span>
          <span className="font-mono text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
            {event.event_type}
          </span>
          {isUnknown && (
            <span
              className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{
                background: 'var(--dash-hover-bg)',
                color: 'var(--dash-text-muted)',
                border: '1px solid var(--dash-border)',
              }}
            >
              Unhandled
            </span>
          )}
          {hasError && (
            <span className="font-mono text-[9px] uppercase tracking-wider text-red-400 bg-red-500/10 border border-red-500/30 px-1.5 py-0.5 rounded">
              Error
            </span>
          )}
        </div>
        {summary && (
          <div className="font-mono text-xs truncate" style={{ color: 'var(--dash-text-muted)' }}>
            {summary}
          </div>
        )}
      </div>

      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        <span
          className={`inline-flex items-center px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider rounded ${
            event.processed
              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
              : ''
          }`}
          style={
            event.processed
              ? undefined
              : {
                  background: 'var(--dash-hover-bg)',
                  color: 'var(--dash-text-muted)',
                  border: '1px solid var(--dash-border)',
                }
          }
        >
          {event.processed ? 'Processed' : 'Pending'}
        </span>
        <span className="font-mono text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
          {new Date(event.received_at).toLocaleString()}
        </span>
      </div>
    </button>
  );
}
